/* eslint-disable react-hooks/exhaustive-deps */
import { Home } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import property_profile_right_image from "../../../assets-user/post-property/property_profile_right_image.png";
import { AMENITIES } from "../../../utils/amenities";
import { AREA_UNITS, INITIAL_AREA } from "../../../utils/constants";
import { showError } from "../../../utils/toastUtils";
import {
  BoxSelector,
  FloorPlan,
  InputField,
  InputWithSelect,
  InputWithUnit,
  RadioField,
  SmallInputField,
  SubtleWrapper,
  TextAreaField,
} from "../../admin/common/form-elements";
import BackHeader from "./BackHeader";
import RightSideInfoCard from "./RightSideInfoCard";

const useFormUpdaters = (formData, setFormData) => {
  const updateField = useCallback(
    (name, value) => {
      setFormData((prev) => {
        if (prev[name] === value) return prev;
        return { ...prev, [name]: value };
      });
    },
    [setFormData]
  );

  const updateNested = useCallback(
    (parentKey, childKey, value) => {
      setFormData((prev) => {
        const parent = prev[parentKey] || {};
        if (parent[childKey] === value) return prev;
        return { ...prev, [parentKey]: { ...parent, [childKey]: value } };
      });
    },
    [setFormData]
  );

  const updateArea = useCallback(
    (areaKey, field, value) => {
      setFormData((prev) => {
        const area = prev[areaKey] || { ...INITIAL_AREA };
        if (area[field] === value) return prev;
        return { ...prev, [areaKey]: { ...area, [field]: value } };
      });
    },
    [setFormData]
  );

  const toggleAmenity = useCallback(
    (amenityKey) => {
      setFormData((prev) => {
        const list = prev.amenities || [];
        const exists = list.find((a) => a.key === amenityKey);
        const next =
          exists === undefined || !exists
            ? [...list, AMENITIES.find((a) => a.key === amenityKey)]
            : list.filter((a) => a.key !== amenityKey);
        return { ...prev, amenities: next };
      });
    },
    [setFormData]
  );

  const replaceArrayField = useCallback(
    (name, arr) => {
      setFormData((prev) => {
        if (prev[name] === arr) return prev;
        return { ...prev, [name]: arr };
      });
    },
    [setFormData]
  );

  return {
    updateField,
    updateNested,
    updateArea,
    toggleAmenity,
    replaceArrayField,
  };
};

const ResidentialFields = React.memo(function ResidentialFields({
  formData,
  updaters,
}) {
  const { updateField } = updaters;

  return (
    <>
      <div className="max-w-3xl space-y-4 mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SubtleWrapper title="Project / Building Name">
            <InputField
              name="projectName"
              value={formData.projectName || ""}
              onChange={(e) => updateField("projectName", e.target.value)}
              placeholder="Project name (optional)"
            />
          </SubtleWrapper>

          <SubtleWrapper title="Builder Name">
            <InputField
              name="builderName"
              value={formData.builderName || ""}
              onChange={(e) => updateField("builderName", e.target.value)}
              placeholder="Builder"
            />
          </SubtleWrapper>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SubtleWrapper title="Brokerage">
            <InputWithSelect
              name="brokerage"
              selectValue={formData.brokerage}
              selectOptions={[
                { label: "0% (No Brokerage)", value: "0%" },
                { label: "0.5%", value: "0.5%" },
                { label: "1%", value: "1%" },
                { label: "1.5%", value: "1.5%" },
                { label: "2%", value: "2%" },
                { label: "2.5%", value: "2.5%" },
                { label: "Custom / Negotiable", value: "custom" },
              ]}
              placeholder="Select Brokerage"
              onSelectChange={(value) => updateField("brokerage", value)}
            />
          </SubtleWrapper>
          <SubtleWrapper title="Availability">
            <RadioField
              name="availability"
              value={formData.availability}
              options={[
                { label: "Ready to move", value: "ready_to_move" },
                { label: "Under Construction", value: "under_construction" },
              ]}
              onChange={(val) => updateField("availability", val)}
            />
          </SubtleWrapper>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SubtleWrapper title="Total Floors">
            <SmallInputField
              type="number"
              value={formData.totalFloors || ""}
              onChange={(e) =>
                updateField("totalFloors", Number(e.target.value))
              }
              placeholder="Total floors"
            />
          </SubtleWrapper>

          <SubtleWrapper title="Property Floor">
            <SmallInputField
              type="number"
              value={formData.propertyFloor || ""}
              onChange={(e) =>
                updateField("propertyFloor", Number(e.target.value))
              }
              placeholder="Floor number"
            />
          </SubtleWrapper>
        </div>
        <SubtleWrapper title="Ownership">
          <RadioField
            name="ownership"
            value={formData.ownership}
            options={[
              { label: "Freehold", value: "freehold" },
              { label: "Co-operative society", value: "co_operative" },
              { label: "Leasehold", value: "leasehold" },
              { label: "Power of attorney", value: "power_of_attorney" },
            ]}
            onChange={(val) => updateField("ownership", val)}
          />
        </SubtleWrapper>
        <SubtleWrapper title="Floor Plan">
          <FloorPlan
            floorPlan={formData.floorPlan}
            onChange={(updatedData) =>
              updateField("floorPlan", updatedData?.[0])
            }
            allowMultiple={false}
          />
        </SubtleWrapper>
      </div>
    </>
  );
});

const CommercialFields = React.memo(function CommercialFields({
  formData,
  updaters,
}) {
  const { updateField, updateArea } = updaters;

  return (
    <>
      <div className="max-w-3xl space-y-4 mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SubtleWrapper title="Property Name">
            <InputField
              name="propertyName"
              value={formData.propertyName || ""}
              onChange={(e) => updateField("propertyName", e.target.value)}
              placeholder="Property / Project name"
            />
          </SubtleWrapper>
          <SubtleWrapper title="Suitable For">
            <InputField
              name="suitableFor"
              value={formData.suitableFor || ""}
              onChange={(e) => updateField("suitableFor", e.target.value)}
              placeholder="E.g. Retail, Office, Warehouse"
            />
          </SubtleWrapper>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SubtleWrapper title="Property Age (years)">
            <SmallInputField
              type="number"
              value={formData.propertyAge || ""}
              onChange={(e) =>
                updateField("propertyAge", Number(e.target.value))
              }
              placeholder="Age in years"
            />
          </SubtleWrapper>
          <SubtleWrapper title="Property Floor">
            <InputField
              type="number"
              name="propertyFloor"
              value={formData.propertyFloor || ""}
              onChange={(e) => updateField("propertyFloor", e.target.value)}
              placeholder="Property is on which floor"
            />
          </SubtleWrapper>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SubtleWrapper title="Carpet Area">
            <InputWithUnit
              type="number"
              value={formData?.carpetArea?.value}
              selectedUnit={formData?.carpetArea?.unit}
              onChange={(e) =>
                updateArea("carpetArea", "value", Number(e.target.value))
              }
              onUnitChange={(u) => updateArea("carpetArea", "unit", u)}
              units={AREA_UNITS}
              placeholder="Enter carpet area"
            />
          </SubtleWrapper>
          <SubtleWrapper title="Super Area">
            <InputWithUnit
              type="number"
              value={formData?.superArea?.value}
              selectedUnit={formData?.superArea?.unit}
              onChange={(e) =>
                updateArea("superArea", "value", Number(e.target.value))
              }
              onUnitChange={(u) => updateArea("superArea", "unit", u)}
              units={AREA_UNITS}
              placeholder="Enter super area"
            />
          </SubtleWrapper>
          <SubtleWrapper title="Price per sqft">
            <SmallInputField
              type="number"
              value={formData.pricePerSqft || ""}
              onChange={(e) =>
                updateField("pricePerSqft", Number(e.target.value))
              }
              placeholder="₹ per sqft"
            />
          </SubtleWrapper>

          <SubtleWrapper title="Expected Price">
            <SmallInputField
              type="number"
              value={formData.expectedPrice || ""}
              onChange={(e) =>
                updateField("expectedPrice", Number(e.target.value))
              }
              placeholder="₹ Expected price"
            />
          </SubtleWrapper>
          <SubtleWrapper title="Transaction Type">
            <InputWithSelect
              selectValue={formData.transactionType}
              selectOptions={[
                { label: "Resale", value: "resale" },
                { label: "Sale", value: "sale" },
                { label: "Lease", value: "lease" },
                { label: "Rent", value: "rent" },
              ]}
              onSelectChange={(val) => updateField("transactionType", val)}
              placeholder="Select transaction type"
            />
          </SubtleWrapper>
          <SubtleWrapper title="Availability">
            <RadioField
              name="availability"
              value={formData.availability}
              options={[
                { label: "Ready to move", value: "ready_to_move" },
                { label: "Under Construction", value: "under_construction" },
              ]}
              onChange={(val) => updateField("availability", val)}
            />
          </SubtleWrapper>
        </div>
      </div>
    </>
  );
});

const LandFields = React.memo(function LandFields({ formData, updaters }) {
  const { updateField, updateArea } = updaters;

  return (
    <>
      <div className="max-w-3xl space-y-4 mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SubtleWrapper title="Property Name">
            <InputField
              name="propertyName"
              value={formData.propertyName || ""}
              onChange={(e) => updateField("propertyName", e.target.value)}
              placeholder="Property / Project name"
            />
          </SubtleWrapper>
          <SubtleWrapper title="Pre-Leased Property">
            <RadioField
              name={`isPreleasedProperty `}
              onChange={(value) => updateField("isPreleasedProperty", value)}
              value={formData?.isPreleasedProperty}
              options={[
                { label: "Yes", value: "true" },
                { label: "No", value: "false" },
              ]}
            />
          </SubtleWrapper>
          <SubtleWrapper title="Road Width">
            <InputWithUnit
              type="number"
              value={formData?.widthOfRoadFacing?.value}
              selectedUnit={formData?.widthOfRoadFacing?.unit}
              onChange={(e) =>
                updateArea("widthOfRoadFacing", "value", Number(e.target.value))
              }
              onUnitChange={(u) => updateArea("widthOfRoadFacing", "unit", u)}
              units={AREA_UNITS}
              placeholder="Road width"
            />
          </SubtleWrapper>
          <SubtleWrapper title="Open Sides">
            <BoxSelector
              totalBoxes={4}
              value={formData.noOfOpenSides}
              onChange={(value) => updateField("noOfOpenSides", Number(value))}
            />{" "}
          </SubtleWrapper>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SubtleWrapper title="Floors Allowed">
            <SmallInputField
              type="number"
              value={formData.floorsAllowed || ""}
              onChange={(e) => updateField("floorsAllowed", e.target.value)}
              placeholder="Floors allowed"
            />
          </SubtleWrapper>
          <SubtleWrapper title="Carpet Area / Plot Area">
            <InputWithUnit
              type="number"
              value={formData?.carpetArea?.value}
              selectedUnit={formData?.carpetArea?.unit}
              onChange={(e) =>
                updateArea("carpetArea", "value", Number(e.target.value))
              }
              onUnitChange={(u) => updateArea("carpetArea", "unit", u)}
              units={AREA_UNITS}
              placeholder="Plot area"
            />
          </SubtleWrapper>
          <SubtleWrapper title="Expected Price">
            <SmallInputField
              type="number"
              name="expectedPrice"
              value={formData.expectedPrice}
              placeholder="₹ Price per sqft."
              onChange={(e) =>
                updateField("expectedPrice", Number(e.target.value))
              }
            />
          </SubtleWrapper>
          <SubtleWrapper title="Price per Sqft">
            <SmallInputField
              type="number"
              name="pricePerSqft"
              value={formData.pricePerSqft}
              placeholder="₹ Price per sqft."
              onChange={(e) =>
                updateField("pricePerSqft", Number(e.target.value))
              }
            />
          </SubtleWrapper>
        </div>
      </div>
    </>
  );
});

const Tab1PropertyDetails = ({ onNext, onBack, formData, setFormData }) => {
  const updaters = useFormUpdaters(formData, setFormData);

  useState(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleContinue = useCallback(() => {
    const missing = [];

    if (!formData.propertyType) missing.push("Property Type");
    if (!formData.description?.trim()) missing.push("Description");
    if (formData.propertyType === "residential") {
      if (!formData.projectName?.trim()) missing.push("Project Name");
      if (!formData.totalFloors) missing.push("Total Floors");
      if (!formData.propertyFloor) missing.push("Property Floor");
      if (!formData.carpetArea?.value) missing.push("Carpet Area");
      if (!formData.floorPlan?.expectedPrice) missing.push("Expected Price");
      if (!formData.floorPlan?.pricePerSqft) missing.push("Price Per Sqft");
    }

    if (formData.propertyType === "commercial") {
      if (!formData.propertyName?.trim()) missing.push("Property Name");
      if (!formData?.pricePerSqft) missing.push("Price Per Sqft ");
      if (!formData?.expectedPrice) missing.push("Expected Price");
      if (!formData?.carpetArea?.value) missing.push("Carpet Area");
      if (!formData?.superArea?.value) missing.push("Super Area");
      if (!formData?.propertyFloor) missing.push("Property Floor");
      if (!formData?.availability) missing.push("Availability");
      if (!formData?.transactionType) missing.push("Transaction Type");
    }

    if (formData.propertyType === "land") {
      if (!formData.propertyName?.trim()) missing.push("Property Name");
      if (!formData.noOfOpenSides) missing.push("No. of Open Sides");
      if (!formData?.expectedPrice) missing.push("Expected Price");
      if (!formData?.carpetArea?.value) missing.push("Carpet Area");
      if (!formData?.pricePerSqft) missing.push("Price Per Sqft");
      if (!formData?.floorsAllowed) missing.push("Floors Allowed");
    }

    if (missing.length > 0) {
      showError(`Please fill out: ${missing[0]}`);
      return;
    }

    onNext();
  }, [formData, onNext]);

  const FieldSection = useMemo(() => {
    switch (formData.propertyType) {
      case "residential":
        return <ResidentialFields formData={formData} updaters={updaters} />;
      case "commercial":
        return <CommercialFields formData={formData} updaters={updaters} />;
      case "land":
        return <LandFields formData={formData} updaters={updaters} />;
      default:
        return (
          <div className="max-w-3xl text-sm text-text-secondary">
            Please select a property type in the previous step to see specific
            fields.
          </div>
        );
    }
  }, [formData.propertyType, formData, updaters]);

  return (
    <div className="flex flex-col xl:flex-row relative w-full no-scrollbar px-3 sm:px-4 md:px-6 lg:px-10 ">
      {/* Left Part */}
      <div className="xl:w-[70%] ">
        {/* Heading */}
        <BackHeader title="Property Details" hideBack={true} />

        <div className="max-w-3xl space-y-3 sm:space-y-4 mx-auto">
          <SubtleWrapper title="Property Type">
            <InputWithSelect
              name="propertyType"
              placeholder="Property Type"
              selectName="propertyType"
              selectValue={formData.propertyType}
              selectOptions={[
                { label: "Residential", value: "residential" },
                { label: "Commercial", value: "commercial" },
                { label: "Land", value: "land" },
              ]}
              onSelectChange={(value) =>
                setFormData((prev) => ({ ...prev, propertyType: value }))
              }
            />
          </SubtleWrapper>

          <SubtleWrapper title={"Description"}>
            <TextAreaField
              placeholder="Type here..."
              name="description"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
          </SubtleWrapper>

          {/* Why To Consider */}
          <SubtleWrapper title="Why should we consider you?">
            <TextAreaField
              name="whyToConsider"
              value={formData.whyToConsider}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  whyToConsider: e.target.value,
                }))
              }
              placeholder="Explain briefly..."
            />
          </SubtleWrapper>
        </div>

        <div className="mt-6 ">{FieldSection}</div>

        {/* Buttons */}
        <div className="flex gap-3 my-8  p-4 md:pb-12">
          <button
            className="flex-1 px-6 py-3 border-2 active:scale-[0.98] border-public-highlight text-public-highlight rounded-full font-medium hover:bg-public-highlight/5 transition-all duration-200"
            onClick={handleContinue}
          >
            Continue
          </button>
        </div>
      </div>

      {/* Right Side Info Block */}
      <div className="xl:w-[30%] mb-10 xl:mb-0 xl:min-h-screen bg-gray-50 sm:px-4  ">
        <div className="xl:sticky top-28">
          <RightSideInfoCard
            icon={Home}
            title="Help Us Match You Better"
            description="Clear property details help us match your listing with the right buyers.
                        The more precise you are, the better your visibility and responses.
                        Accurate profiles build trust and attract genuinely interested people."
            image={property_profile_right_image}
          />
        </div>
      </div>
    </div>
  );
};

export default Tab1PropertyDetails;
